import { checkParticipants } from '../../components/waiver-steps-2/helpers'
import isMobile from 'is-mobile'

export default function waiverDataModel(metadata, queryParamsObject, waiverContentItems, extraAppFeatures) {
  let waiverModel = {}
  const metadataObject = queryParamsObject.waiverDataId ? metadata.waiverData : queryParamsObject
  if (!metadataObject) return {}
  const isParticipantMinor = metadataObject.isParticipantMinor || checkParticipants(metadataObject)

  if (metadataObject.firstName) {
    if (metadataObject.isParticipantMinor) {
      metadataObject.minors = [
        {
          firstName: metadataObject.firstName,
          lastName: metadataObject.lastName
        }
      ]
    } else {
      metadataObject.adult = {
        firstName: metadataObject.firstName,
        lastName: metadataObject.lastName
      }
    }
    delete metadataObject.firstName
    delete metadataObject.lastName
    delete metadataObject.isParticipantMinor
  }
  const isEhs = extraAppFeatures.includes('EmployeeHealthSafety')
  const productLine = metadataObject.productLine
  const customerOrderId = metadataObject.customerOrderId
  const waiverContentItem = waiverContentItems && waiverContentItems.find((item) => item.productLine === productLine)
  const isClassicSigned = extraAppFeatures.includes('SignedWebWaivers')
  const isGuest =
    metadataObject && metadataObject.guest && (metadataObject.guest === 'true' || metadataObject.guest === true)
  const isMobileDevice = isMobile({ tablet: true })
  const classNameParam = 'm-x5'
  const cancellationDate = metadataObject.cancellationDate
  const cancellationAuthorFirstName = metadataObject.cancellationAuthorFirstName
  const cancellationAuthorLastName = metadataObject.cancellationAuthorLastName
  const obtainSignature = isClassicSigned || isEhs || isGuest
  const driverLicenseInfoRequired = !(isEhs || isGuest)
  waiverModel = {
    isEhs,
    isGuest,
    isClassicSigned,
    isParticipantMinor,
    isMobileDevice,
    obtainSignature,
    driverLicenseInfoRequired,
    customerOrderId,
    productLine,
    cancellationDate,
    cancellationAuthorFirstName,
    cancellationAuthorLastName
  }
  const { minors } = metadataObject
  let minor1, minor2, minor3, minor4, minor5
  if (minors && minors.length > 0) {
    minor1 = minors[0]
    minor2 = minors[1]
    minor3 = minors[2]
    minor4 = minors[3]
    minor5 = minors[4]
  }

  const contactInfo = {
    stepNumber: 101,
    label: isGuest && checkParticipants(metadataObject) ? 'driverGuardianContactInfo' : 'driverContactInfo', // based on modes
    hidden: false,
    firstName: {
      type: 'string',
      fieldName: 'firstName',
      className: classNameParam,
      readonly: !!(metadataObject.adult && metadataObject.adult.firstName),
      hidden: false,
      required: true,
      label: 'firstName',
      validation: 'firstNameValidation',
      initialValue: metadataObject.adult && metadataObject.adult.firstName
    },

    lastName: {
      type: 'string',
      fieldName: 'lastName',
      className: classNameParam,
      readonly: !!(metadataObject.adult && metadataObject.adult.lastName),
      hidden: false,
      required: true,
      label: 'lastName',
      validation: 'lastNameValidation',
      initialValue: metadataObject.adult && metadataObject.adult.lastName
    },

    email: {
      type: 'string',
      fieldName: 'email',
      className: classNameParam,
      readonly: !!metadataObject.email,
      hidden: false,
      required: true,
      label: 'email',
      validation: 'emailValidation',
      initialValue: metadataObject.email
    },

    phone: {
      type: 'string',
      fieldName: 'phone',
      className: classNameParam,
      readonly: false,
      hidden: false,
      required: true,
      label: 'phone',
      validation: 'phoneValidation',
      initialValue: metadataObject.phone
    },

    addressLine1: {
      type: 'string',
      fieldName: 'addressLine1',
      className: classNameParam,
      readonly: false,
      hidden: false,
      required: true,
      label: 'addressLine1',
      validation: 'addressLine1Validation',
      initialValue: metadataObject.addressLine1
    },

    addressLine2: {
      type: 'string',
      fieldName: 'addressLine2',
      className: classNameParam,
      readonly: false,
      hidden: false,
      required: false,
      label: 'addressLine2',
      validation: 'addressLine2Validation',
      initialValue: metadataObject.addressLine2
    },

    city: {
      type: 'string',
      fieldName: 'city',
      className: classNameParam,
      readonly: false,
      hidden: false,
      required: true,
      label: 'city',
      validation: 'cityValidation',
      initialValue: metadataObject.city
    },

    state: {
      type: 'string',
      fieldName: 'state',
      className: classNameParam,
      enum: 'state',
      readonly: false,
      hidden: false,
      required: true,
      label: 'state',
      validation: 'stateValidation',
      initialValue: metadataObject.state
    },

    postalCode: {
      type: 'string',
      fieldName: 'postalCode',
      className: classNameParam,
      readonly: false,
      hidden: false,
      required: true,
      label: 'postalCode',
      validation: 'postalCodeValidation',
      initialValue: metadataObject.postalCode
    },

    country: {
      type: 'string',
      fieldName: 'country',
      className: classNameParam,
      enum: 'country',
      readonly: false,
      hidden: false,
      required: true,
      label: 'country',
      validation: 'countryValidation',
      initialValue: metadataObject.country || 'US'
    },

    birthDate: {
      type: 'date',
      fieldName: 'birthDate',
      className: classNameParam,
      readonly: false,
      hidden: !isGuest,
      required: isGuest,
      label: 'birthDate',
      validation: 'birthDateValidation',
      initialValue: metadataObject.birthDate
    },

    firstTimeRider: {
      type: 'boolean',
      fieldName: 'firstTimeRider',
      className: classNameParam,
      readonly: false,
      hidden: isEhs,
      required: false,
      label: 'firstTimeRider',
      validation: 'phoneValidation',
      initialValue: metadataObject.firstTimeRider
    },

    driverHasMotorcycleLicense: {
      type: 'boolean',
      fieldName: 'driverHasMotorcycleLicense',
      className: classNameParam,
      readonly: false,
      hidden: !isGuest || !['IND', 'SLG'].includes(productLine),
      required: false, // ['IND', 'SLG'].includes(productLine),
      label: ['IND', 'SLG'].includes(productLine)
        ? 'licensedToOperateMotorcycle'
        : 'licensedToOperateMotorcycle_not_required',
      validation: 'driversLicenseValidation',
      initialValue: metadataObject.driverHasMotorcycleLicense
    },

    buttons: [
      {
        type: 'button',
        label: 'next',
        variant: 'contained',
        color: 'primary',
        size: 'large',
        className: 'mt-2',
        clickFunc: 'nextStepClick'
      }
    ]
  }
  const driverLicenseInfo = {
    stepNumber: 200,
    label: 'driverLicenseInfo',
    hidden: isGuest || isEhs,
    birthDate: {
      type: 'date',
      fieldName: 'birthDate',
      className: classNameParam,
      readonly: false,
      hidden: false,
      required: true,
      label: 'birthDate',
      validation: 'birthDateValidation',
      initialValue: metadataObject.birthDate
    },

    driversLicenseState: {
      type: 'string',
      enum: 'state',
      fieldName: 'driversLicenseState',
      className: classNameParam,
      readonly: false,
      hidden: false,
      required: true,
      label: 'driversLicenseState',
      validation: 'driversLicenseStateValidation',
      initialValue: metadataObject.driversLicenseState
    },

    driversLicenseCountry: {
      type: 'string',
      enum: 'country',
      fieldName: 'driversLicenseCountry',
      className: classNameParam,
      readonly: false,
      hidden: false,
      required: true,
      label: 'driversLicenseCountry',
      validation: 'driversLicenseCountryValidation',
      initialValue: metadataObject.driversLicenseCountry || 'US'
    },

    driversLicense: {
      type: 'string',
      fieldName: 'driversLicense',
      className: classNameParam,
      readonly: false,
      hidden: false,
      required: true,
      label: 'driversLicense',
      validation: 'driversLicenseValidation',
      initialValue: metadataObject.driversLicense
    },

    driverHasMotorcycleLicense: {
      type: 'boolean',
      fieldName: 'driverHasMotorcycleLicense',
      className: classNameParam,
      readonly: false,
      hidden: !['IND', 'SLG'].includes(productLine),
      required: false, // ['IND', 'SLG'].includes(productLine),
      label: ['IND', 'SLG'].includes(productLine)
        ? 'licensedToOperateMotorcycle'
        : 'licensedToOperateMotorcycle_not_required',
      validation: 'driversLicenseValidation',
      initialValue: metadataObject.driverHasMotorcycleLicense
    },

    buttons: [
      {
        type: 'button',
        label: 'back',
        variant: 'contained',
        color: 'primary',
        size: 'large',
        className: 'mt-2 mh-2',
        clickFunc: 'prevStep'
      },
      {
        type: 'button',
        label: 'next',
        variant: 'contained',
        color: 'primary',
        size: 'large',
        className: 'mt-2',
        clickFunc: 'nextStepClick'
      }
    ]
  }
  const minorsInfo = {
    stepNumber: 300,
    label: (isGuest && isParticipantMinor) || isEhs ? 'participants' : 'additionalParticipants',
    hidden: false,
    minorParticipantsCheckbox: {
      type: 'boolean',
      readonly: false,
      fieldName: 'minorParticipantsCheckbox',
      className: 'no-flex down-1 full-width',
      hidden: !(isEhs || (isGuest && checkParticipants(metadataObject))),
      required: isEhs || (isGuest && checkParticipants(metadataObject)),
      label: isGuest && checkParticipants(metadataObject) ? 'minorListGuest' : 'minorList',
      validation: 'minorParticipantsCheckboxValidation',
      initialValue: ''
    },

    minorParticipantsAccompaniedCheckbox: {
      type: 'boolean',
      readonly: false,
      fieldName: 'minorParticipantsAccompaniedCheckbox',
      className: 'no-flex down-1 full-width',
      hidden: !isGuest,
      required: isGuest,
      label: 'minorsAccompanied',
      validation: 'minorParticipantsAccompaniedCheckboxValidation',
      initialValue: ''
    },

    minorParticipantsNotDriveCheckbox: {
      type: 'boolean',
      readonly: false,
      fieldName: 'minorParticipantsNotDriveCheckbox',
      className: 'no-flex down-1 full-width',
      hidden: !isGuest,
      required: isGuest,
      label: 'minorsNotDrive',
      validation: 'minorParticipantsNotDriveCheckboxValidation',
      initialValue: ''
    },

    minorParticipantsPassengersCheckbox: {
      type: 'boolean',
      readonly: false,
      fieldName: 'minorParticipantsPassengersCheckbox',
      className: 'no-flex down-1 full-width',
      hidden: !isGuest,
      required: isGuest,
      label: 'minorsPassengers',
      validation: 'minorParticipantsPassengersCheckboxValidation',
      initialValue: ''
    },

    minorParticipantsBySignCheckbox: {
      type: 'boolean',
      readonly: false,
      fieldName: 'minorParticipantsBySignCheckbox',
      className: 'no-flex down-1 full-width',
      hidden: !isGuest,
      required: isGuest,
      label: 'minorsBySign',
      validation: 'minorParticipantsBySignCheckboxValidation',
      initialValue: ''
    },
    participants: [
      {
        firstName: {
          type: 'string',
          readonly: false,
          fieldName: 'firstName',
          className: classNameParam,
          hidden: false,
          required: false,
          label: 'firstName',
          validation: 'firstNameValidation',
          initialValue: (minor1 && minor1.firstName) || ''
        },

        lastName: {
          type: 'string',
          readonly: false,
          fieldName: 'lastName',
          className: classNameParam,
          hidden: false,
          required: false,
          label: 'lastName',
          validation: 'lastNameValidation',
          initialValue: (minor1 && minor1.lastName) || ''
        },

        isUnder18: {
          type: 'boolean',
          readonly: false,
          fieldName: 'isUnder18',
          className: classNameParam,
          hidden: isEhs || checkParticipants(metadataObject),
          required: false,
          label: 'under18',
          validation: 'isUnder18Validation',
          initialValue: false
        }
      },
      {
        firstName: {
          type: 'string',
          readonly: false,
          fieldName: 'firstName',
          className: classNameParam,
          hidden: false,
          required: false,
          label: 'firstName',
          validation: 'firstNameValidation',
          initialValue: (minor2 && minor2.firstName) || ''
        },

        lastName: {
          type: 'string',
          readonly: false,
          fieldName: 'lastName',
          className: classNameParam,
          hidden: false,
          required: false,
          label: 'lastName',
          validation: 'lastNameValidation',
          initialValue: (minor2 && minor2.lastName) || ''
        },

        isUnder18: {
          type: 'boolean',
          readonly: false,
          fieldName: 'isUnder18',
          className: classNameParam,
          hidden: isEhs || checkParticipants(metadataObject),
          required: true,
          label: 'under18',
          validation: 'isUnder18Validation',
          initialValue: false
        }
      },
      {
        firstName: {
          type: 'string',
          readonly: false,
          fieldName: 'firstName',
          className: classNameParam,
          hidden: false,
          required: false,
          label: 'firstName',
          validation: 'firstNameValidation',
          initialValue: (minor3 && minor3.firstName) || ''
        },

        lastName: {
          type: 'string',
          readonly: false,
          fieldName: 'lastName',
          className: classNameParam,
          hidden: false,
          required: false,
          label: 'lastName',
          validation: 'lastNameValidation',
          initialValue: (minor3 && minor3.lastName) || ''
        },

        isUnder18: {
          type: 'boolean',
          readonly: false,
          fieldName: 'isUnder18',
          className: classNameParam,
          hidden: isEhs || checkParticipants(metadataObject),
          required: true,
          label: 'under18',
          validation: 'isUnder18Validation',
          initialValue: false
        }
      },
      {
        firstName: {
          type: 'string',
          readonly: false,
          fieldName: 'firstName',
          className: classNameParam,
          hidden: !(minor4 && minor4.firstName),
          required: false,
          label: 'firstName',
          validation: 'firstNameValidation',
          initialValue: (minor4 && minor4.firstName) || ''
        },

        lastName: {
          type: 'string',
          readonly: false,
          fieldName: 'lastName',
          className: classNameParam,
          hidden: !(minor4 && minor4.lastName),
          required: false,
          label: 'lastName',
          validation: 'lastNameValidation',
          initialValue: (minor4 && minor4.lastName) || ''
        },

        isUnder18: {
          type: 'boolean',
          readonly: false,
          fieldName: 'isUnder18',
          className: classNameParam,
          hidden: isEhs || checkParticipants(metadataObject),
          required: true,
          label: 'under18',
          validation: 'isUnder18Validation',
          initialValue: false
        }
      },
      {
        firstName: {
          type: 'string',
          readonly: false,
          fieldName: 'firstName',
          className: classNameParam,
          hidden: !(minor5 && minor5.firstName),
          required: false,
          label: 'firstName',
          validation: 'firstNameValidation',
          initialValue: (minor5 && minor5.firstName) || ''
        },

        lastName: {
          type: 'string',
          readonly: false,
          fieldName: 'lastName',
          className: classNameParam,
          hidden: !(minor5 && minor5.lastName),
          required: false,
          label: 'lastName',
          validation: 'lastNameValidation',
          initialValue: (minor5 && minor5.lastName) || ''
        },

        isUnder18: {
          type: 'boolean',
          readonly: false,
          fieldName: 'isUnder18',
          className: classNameParam,
          hidden: isEhs || checkParticipants(metadataObject),
          required: true,
          label: 'under18',
          validation: 'isUnder18Validation',
          initialValue: false
        }
      }
    ],

    buttons: [
      {
        type: 'button',
        label: 'back',
        variant: 'contained',
        color: 'primary',
        size: 'large',
        className: 'mt-2 mh-2',
        clickFunc: 'prevStep'
      },
      {
        type: 'button',
        label: 'next',
        variant: 'contained',
        color: 'primary',
        size: 'large',
        className: 'mt-2',
        clickFunc: 'nextStepClick'
      }
    ]
  }
  const reviewAgreement = {
    stepNumber: 400,
    label: 'reviewAgreement',
    hidden: false,
    waiverAgreementPdfUrlSigned: {
      type: 'string',
      readonly: false,
      fieldName: 'waiverAgreementPdfUrlSigned',
      className: 'no-flex down-1 full-width',
      hidden: false,
      required: true,
      label: 'waiverAgreementPdfUrlSigned',
      validation: 'waiverAgreementPdfUrlSignedValidation',
      initialValue: waiverContentItem && waiverContentItem.waiverAgreementPdfUrlSigned
    },

    waiverAgreementPdfUrl: {
      type: 'string',
      readonly: false,
      fieldName: 'waiverAgreementPdfUrl',
      className: 'no-flex down-1 full-width',
      hidden: false,
      required: true,
      label: 'waiverAgreementPdfUrl',
      validation: 'waiverAgreementPdfUrlValidation',
      initialValue: waiverContentItem && waiverContentItem.waiverAgreementPdfUrl
    },

    waiverAgreement: {
      type: 'string',
      readonly: false,
      fieldName: 'waiverAgreement',
      className: 'no-flex down-1 full-width',
      hidden: false,
      required: true,
      label: 'waiverAgreement',
      validation: 'waiverAgreementValidation',
      initialValue: waiverContentItem && waiverContentItem.waiverAgreement
    },

    waiverAgreementCheck: {
      type: 'boolean',
      readonly: false,
      fieldName: 'waiverAgreementCheck',
      className: 'no-flex down-1 full-width',
      hidden: false,
      required: true,
      label: 'readAgreementCarefully',
      validation: 'waiverAgreementCheckValidation',
      initialValue: '',
      labelClassName: 'bold'
    },

    optInCheck: {
      type: 'boolean',
      readonly: false,
      fieldName: 'optInCheck',
      className: 'no-flex down-1 full-width',
      hidden: isEhs,
      required: !isEhs,
      label: 'emailOptIn',
      validation: 'receivePromotionalEmailsValidation',
      initialValue: ''
    },

    overviewQ1Check: {
      type: 'boolean',
      readonly: false,
      fieldName: 'overviewQ1',
      className: 'no-flex down-1 full-width',
      hidden: false,
      required: true,
      label: waiverContentItem && waiverContentItem.overviewQ1,
      validation: 'overviewQ1CheckValidation',
      initialValue: ''
    },

    overviewQ2Check: {
      type: 'boolean',
      readonly: false,
      fieldName: 'overviewQ2',
      className: 'no-flex down-1 full-width',
      hidden: false,
      required: true,
      label: waiverContentItem && waiverContentItem.overviewQ2,
      validation: 'overviewQ2CheckValidation',
      initialValue: ''
    },

    overviewQ3Check: {
      type: 'boolean',
      readonly: false,
      fieldName: 'overviewQ3',
      className: 'no-flex down-1 full-width',
      hidden: false,
      required: true,
      label: waiverContentItem && waiverContentItem.overviewQ3,
      validation: 'overviewQ3CheckValidation',
      initialValue: ''
    },

    overviewQ4Check: {
      type: 'boolean',
      readonly: false,
      fieldName: 'overviewQ4',
      className: 'no-flex down-1 full-width',
      hidden: false,
      required: true,
      label: waiverContentItem && waiverContentItem.overviewQ4,
      validation: 'overviewQ4CheckValidation',
      initialValue: ''
    },

    overviewQ5Check: {
      type: 'boolean',
      readonly: false,
      fieldName: 'overviewQ5',
      className: 'no-flex down-1 full-width',
      hidden: false,
      required: true,
      label: waiverContentItem && waiverContentItem.overviewQ5,
      validation: 'overviewQ5CheckValidation',
      initialValue: ''
    },

    overviewQ6Check: {
      type: 'boolean',
      readonly: false,
      fieldName: 'overviewQ6',
      className: 'no-flex down-1 full-width',
      hidden: false,
      required: true,
      label: waiverContentItem && waiverContentItem.overviewQ6,
      validation: 'overviewQ6CheckValidation',
      initialValue: ''
    },

    buttons: [
      {
        type: 'button',
        label: 'back',
        variant: 'contained',
        color: 'primary',
        size: 'large',
        className: 'mt-2 mh-2',
        id: 'review-agreement-button-back',
        clickFunc: 'prevStep'
      },
      {
        type: 'button',
        label: 'next',
        variant: 'contained',
        color: 'primary',
        size: 'large',
        className: 'mt-2',
        id: 'review-agreement-button-next',
        clickFunc: 'nextStepClick'
      }
    ]
  }
  const safetyVideo = {
    stepNumber: 500,
    label:
      waiverContentItem && waiverContentItem.safetyVideoUrl ? 'safetyVideo' : obtainSignature ? 'signature' : 'safetyVideo',
    hidden: false,
    safetyVideoUrl: {
      type: 'string',
      readonly: false,
      hidden: false,
      required: true,
      label: 'safetyVideoUrl',
      validation: 'safetyVideoUrlValidation',
      initialValue: waiverContentItem && waiverContentItem.safetyVideoUrl
    },

    safetyVideoRequired: {
      type: 'boolean',
      fieldName: 'safetyVideoRequired',
      readonly: false,
      hidden: false,
      required: true,
      label: 'safetyVideoRequired',
      validation: 'safetyVideoRequiredValidation',
      initialValue: waiverContentItem && !!waiverContentItem.safetyVideoUrl
    },

    obtainSignature: {
      type: 'string',
      readonly: false,
      hidden: false,
      required: true,
      label: 'obtainSignature',
      validation: 'obtainSignatureValidation',
      initialValue: obtainSignature
    },

    buttons: [
      {
        type: 'button',
        label: 'back',
        variant: 'contained',
        color: 'primary',
        size: 'large',
        className: 'mt-2 mh-2',
        clickFunc: 'prevStep'
      },
      {
        type: 'button',
        label: 'complete',
        variant: 'contained',
        color: 'primary',
        size: 'large',
        className: 'mt-2',
        clickFunc: 'completedClick'
      }
    ]
  }
  const licenseScan = {
    stepNumber: 100,
    label: 'licenseScanLabel',
    hidden: !(isClassicSigned && isMobileDevice),
    holdPhoneText: 'holdPhoneText',
    centerFrontLicenseText: 'centerFrontLicenseText',
    scanDriverLicense: 'scanDriverLicense'
  }

  waiverModel.contactInfo = contactInfo
  waiverModel.driverLicenseInfo = driverLicenseInfo
  waiverModel.minorsInfo = minorsInfo
  waiverModel.reviewAgreement = reviewAgreement
  waiverModel.safetyVideo = safetyVideo
  waiverModel.licenseScan = licenseScan

  return waiverModel
}

export function waiverDataModelUpdateWithProductLine(waiverModel, productLine, waiverContentItems) {
  const waiverContentItem = waiverContentItems.find((item) => item.productLine === productLine)

  if (waiverModel && waiverModel.driverLicenseInfo) {
    waiverModel.driverLicenseInfo.driverHasMotorcycleLicense.hidden = !['IND', 'SLG'].includes(productLine)
    waiverModel.driverLicenseInfo.driverHasMotorcycleLicense.required = ['IND', 'SLG'].includes(productLine)
    waiverModel.driverLicenseInfo.driverHasMotorcycleLicense.label = ['IND', 'SLG'].includes(productLine)
      ? 'licensedToOperateMotorcycle'
      : 'licensedToOperateMotorcycle_not_required'
  }

  if (waiverModel && waiverModel.safetyVideo) {
    waiverModel.safetyVideo.safetyVideoUrl.initialValue = waiverContentItem && waiverContentItem.safetyVideoUrl
    waiverModel.safetyVideo.safetyVideoRequired.initialValue = waiverContentItem && !!waiverContentItem.safetyVideoUrl
  }

  return waiverModel
}
